import { fetchWithAuth, fetchFormDataWithAuth } from './client';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export type SimulatedMode = 'true' | 'false' | 'auto';

export interface Agent {
  id: number;
  name: string;
  description: string | null;
  imageUrl: string | null;
  active: boolean;
  // Paper mode for the manual order route: only 'false' places real orders; 'true' and the
  // column default 'auto' are both paper.
  simulated: SimulatedMode;
  // Simulated (paper) trade count and simulated Total P&L.
  simTradeCount: number;
  simPnl: number;
  profitLoss: number | null;
  realizedProfitLoss: number | null;
  numberOfTrades: number | null;
  // When the agent last traded (ISO string), or null if never.
  lastTradeAt: string | null;
}

export interface AgentPerformance {
  profitLoss: number;
  positionsValue: number;
  numberOfTrades: number;
  numberOfBuyTrades: number;
  numberOfSellTrades: number;
  realizedWonTrades: number;
  realizedLostTrades: number;
  realizedSplitTrades: number;
  realizedProfitLoss: number;
  totalFeesPaid: number;
  createdAt: string;
}

export interface AgentFormData {
  name: string;
  description?: string;
  imageUrl?: string;
  active?: boolean;
  simulated?: SimulatedMode;
}

export const agentsApi = {
  list: () => fetchWithAuth<ApiResponse<Agent[]>>('/agents'),
  get: (id: number) => fetchWithAuth<ApiResponse<Agent>>(`/agents/${id}`),
  getPerformance: (id: number) => fetchWithAuth<ApiResponse<AgentPerformance | null>>(`/agents/${id}/performance`),
  create: (data: AgentFormData) =>
    fetchWithAuth<ApiResponse<Agent>>('/agents', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
  update: (id: number, data: AgentFormData) =>
    fetchWithAuth<ApiResponse<Agent>>(`/agents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
  delete: (id: number) => fetchWithAuth<{ success: boolean }>(`/agents/${id}`, { method: 'DELETE' }),
  uploadImage: async (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return fetchFormDataWithAuth<{ success: boolean; imageUrl?: string }>('/agents/upload-image', formData);
  }
};

export const ordersApi = {
  deleteAllByAgent: (agentId: number) =>
    fetchWithAuth<{ success: boolean; count: number }>(`/orders/${agentId}`, { method: 'DELETE' })
};

export const tradesApi = {
  deleteAllByAgent: (agentId: number) =>
    fetchWithAuth<{ success: boolean; count: number }>(`/trades/${agentId}`, { method: 'DELETE' })
};
